<template>
	<div class="container-fluid mt-2">
		<PageHeader
			:data="{
				name: 'Chris Willow Tracking',
				breadcrumbs: [{ name: 'Home', to: '/' }, { name: 'Marketing Tools', to: '/marketing/chris-willow' }],
			}"
		/>

		<div class="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
			<div class="d-flex align-items-center">
				<label class="me-2 mb-0 text-muted small">Brand</label>
				<select class="form-select form-select-sm" style="width: 200px" v-model="selectedBrandId" :disabled="!brands.length || summaryLoading" @change="loadBrand">
					<option v-for="b in brands" :key="b.id" :value="b.id">{{ b.name }}</option>
				</select>
				<span v-if="summaryLoading" class="ms-2"><span class="spinner-border spinner-border-sm align-middle" role="status"></span></span>
				<button class="btn btn-sm btn-outline-secondary ms-2" :disabled="summaryLoading || visitorsLoading" @click="loadBrand">Refresh</button>
			</div>
			<div v-if="selectedBrand">
				<span class="badge" :class="isInstalled ? 'bg-success' : 'bg-secondary'">{{ isInstalled ? 'Installed' : 'Not installed' }}</span>
				<button class="btn btn-sm btn-outline-secondary ms-2" :disabled="statusSaving" @click="toggleInstalled">{{ isInstalled ? 'Mark not installed' : 'Mark installed' }}</button>
			</div>
		</div>

		<div v-if="errorMessage" class="alert alert-warning py-2 small">{{ errorMessage }}</div>

		<!-- Installed: collapsed confirmation, snippet hidden to keep the page clean -->
		<div v-if="isInstalled && !showSnippet" class="card mb-4 border-success">
			<div class="card-body py-2 d-flex justify-content-between align-items-center flex-wrap gap-2">
				<div class="small">
					<span class="text-success fw-bold">Tracking is installed and receiving hits.</span>
					The numbers below update as visitors arrive and identify themselves on a quote form.
				</div>
				<button class="btn btn-sm btn-outline-secondary" @click="showSnippet = true">Show snippet / troubleshoot</button>
			</div>
		</div>

		<!-- Not installed, or explicitly revealed: install steps, kept dead simple -->
		<div v-else class="card mb-4 border-primary">
			<div class="card-header fw-bold bg-light d-flex justify-content-between align-items-center">
				<span>Install on {{ selectedBrand ? selectedBrand.name : 'this brand' }} (about 2 minutes)</span>
				<button v-if="isInstalled" class="btn btn-sm btn-outline-secondary py-0" @click="showSnippet = false">Hide</button>
			</div>
			<div class="card-body">
				<ol class="mb-3 ps-3">
					<li class="mb-1">Copy the snippet below.</li>
					<li class="mb-1">Paste it once into the site's <code>&lt;head&gt;</code>, or add it as a GTM Custom HTML tag set to fire on <strong>All Pages</strong>.</li>
					<li class="mb-1">Save and publish. That is it, the same snippet works on Town Web (Juniper head), HeyGov, and ClerkMinutes (Framer custom code).</li>
				</ol>
				<div class="position-relative">
					<pre class="bg-light border rounded p-3 small mb-2" style="white-space: pre-wrap; word-break: break-word">{{ installSnippet }}</pre>
					<button class="btn btn-sm btn-primary" @click="copySnippet">{{ copied ? 'Copied' : 'Copy snippet' }}</button>
				</div>
				<div class="small text-muted mt-2">
					Site key for this brand: <code>{{ selectedBrand ? selectedBrand.cw_site_key : '-' }}</code>.
					Posts to <code>{{ endpoint }}</code>.
				</div>
				<div v-if="!isInstalled" class="small text-muted mt-2">
					This switches to <span class="fw-bold">Installed</span> automatically once the first hit arrives, no need to mark it by hand.
				</div>
				<div v-else class="small text-muted mt-3 border-top pt-2">
					<div class="fw-bold mb-1">Not seeing data?</div>
					<ul class="ps-3 mb-0">
						<li>Confirm the GTM container or Framer site was published, not just saved.</li>
						<li>Check this brand's <strong>Domain</strong> on the Brands page matches the live host, or clear it while testing.</li>
						<li>Load a page on the site, then reload this dashboard. Hits can take a few seconds to arrive.</li>
					</ul>
				</div>
			</div>
		</div>

		<!-- Live dashboard -->
		<div class="row g-3 mb-3">
			<div class="col-md-3 col-6">
				<div class="card h-100"><div class="card-body py-3 text-center">
					<div class="text-muted small">Tracked visitors</div>
					<div class="fs-4 fw-bold mt-1">{{ (summary.tracked_visitors || 0).toLocaleString() }}</div>
				</div></div>
			</div>
			<div class="col-md-3 col-6">
				<div class="card h-100"><div class="card-body py-3 text-center">
					<div class="text-muted small">Identified leads</div>
					<div class="fs-4 fw-bold mt-1 text-success">{{ (summary.identified_leads || 0).toLocaleString() }}</div>
					<div class="text-muted" style="font-size: 11px">{{ (summary.matched_crm || 0).toLocaleString() }} matched to CRM</div>
				</div></div>
			</div>
			<div class="col-md-3 col-6">
				<div class="card h-100"><div class="card-body py-3 text-center">
					<div class="text-muted small">Quotes requested</div>
					<div class="fs-4 fw-bold mt-1">{{ (summary.quotes || 0).toLocaleString() }}</div>
				</div></div>
			</div>
			<div class="col-md-3 col-6">
				<div class="card h-100"><div class="card-body py-3 text-center">
					<div class="text-muted small">Cross-property journeys</div>
					<div class="fs-4 fw-bold mt-1">{{ (summary.cross_property || 0).toLocaleString() }}</div>
				</div></div>
			</div>
		</div>

		<div class="card mb-5">
			<div class="card-header fw-bold py-2 d-flex justify-content-between align-items-center flex-wrap gap-2">
				<span>Tracked visitors <span v-if="visitorsLoading" class="spinner-border spinner-border-sm align-middle ms-1" role="status"></span></span>
				<div class="btn-group btn-group-sm" role="group">
					<button class="btn" :class="!filterIdentified ? 'btn-primary' : 'btn-outline-secondary'" @click="filterIdentified = false">All</button>
					<button class="btn" :class="filterIdentified ? 'btn-primary' : 'btn-outline-secondary'" @click="filterIdentified = true">Identified only</button>
				</div>
			</div>
			<table v-if="filteredVisitors.length" class="table table-sm table-hover align-middle mb-0">
				<thead>
					<tr>
						<th style="width: 24px"></th>
						<th>Visitor</th>
						<th>Source</th>
						<th>Campaign</th>
						<th class="text-end">Pages</th>
						<th>Location</th>
						<th>Confidence</th>
						<th class="text-end">Last seen</th>
					</tr>
				</thead>
				<tbody>
					<template v-for="v in filteredVisitors">
						<tr :key="v.id" style="cursor: pointer" :class="{ 'table-active': expandedId === v.id }" @click="toggleVisitor(v.id)">
							<td class="text-muted">{{ expandedId === v.id ? '▾' : '▸' }}</td>
							<td>
								<template v-if="v.email">{{ v.email }} <span v-if="v.client_contact_id" class="badge bg-info ms-1" title="Matched to a CRM contact">CRM</span></template>
								<span v-else class="text-muted">Anonymous</span>
							</td>
							<td>{{ v.utm_source || (v.first_referrer ? shortRef(v.first_referrer) : 'direct') }}{{ v.via ? ' (via ' + v.via + ')' : '' }}</td>
							<td>{{ v.utm_campaign || '-' }}</td>
							<td class="text-end">{{ v.pageviews || 0 }}</td>
							<td>{{ formatLocation(v) }}</td>
							<td><span class="badge" :class="confidenceClass(v.confidence)">{{ v.confidence || 'none' }}</span></td>
							<td class="text-end">{{ formatDate(v.last_seen_at) }}</td>
						</tr>
						<tr v-if="expandedId === v.id" :key="'j' + v.id">
							<td></td>
							<td colspan="7" class="bg-light">
								<div v-if="journeyLoading" class="text-muted small py-2">Loading journey...</div>
								<div v-else-if="journeyEvents.length" class="py-2">
									<div class="small fw-bold mb-2">Journey, {{ journeyEvents.length }} event{{ journeyEvents.length === 1 ? '' : 's' }}</div>
									<ol class="mb-0 ps-3 small">
										<li v-for="(e, i) in journeyEvents" :key="i" class="mb-1">
											<span class="text-muted">{{ formatDateTime(e.occurred_at) }}</span>
											<span class="badge bg-secondary ms-1">{{ e.event_type }}</span>
											<code class="ms-1">{{ e.path || e.url || '-' }}</code>
											<span v-if="e.utm_campaign" class="text-muted ms-1">· campaign: {{ e.utm_campaign }}</span>
											<span v-if="e.utm_source" class="text-muted ms-1">· source: {{ e.utm_source }}</span>
											<span v-if="e.referrer" class="text-muted ms-1">· ref: {{ shortRef(e.referrer) }}</span>
										</li>
									</ol>
								</div>
								<div v-else class="text-muted small py-2">No detailed events recorded for this visitor.</div>
							</td>
						</tr>
					</template>
				</tbody>
			</table>
			<div v-else class="card-body text-center text-muted py-5">
				<div class="mb-1">{{ filterIdentified ? 'No identified visitors yet.' : 'No tracked visitors yet.' }}</div>
				<div class="small">Once the snippet is live, every visitor appears here. Click a visitor to see their full journey: the pages they viewed, in order, and what brought them.</div>
			</div>
		</div>
	</div>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import restApi from '../../api'
import PageHeader from '@/components/layout/PageHeader.vue'

export default {
	name: 'ChrisWillow',
	components: { PageHeader },
	data() {
		return {
			brands: [],
			selectedBrandId: null,
			endpoint: 'https://mc2api.townweb.com/hooks/track',
			summary: { tracked_visitors: 0, identified_leads: 0, quotes: 0, cross_property: 0, recent: [] },
			summaryLoading: false,
			statusSaving: false,
			copied: false,
			showSnippet: false,
			visitors: [],
			visitorsLoading: false,
			filterIdentified: false,
			expandedId: null,
			journeyEvents: [],
			journeyLoading: false,
			errorMessage: '',
		}
	},
	computed: {
		...mapState(['authUser']),
		...mapGetters(['isAuth']),
		selectedBrand() {
			return this.brands.find(b => b.id === this.selectedBrandId) || null
		},
		isInstalled() {
			return this.selectedBrand && this.selectedBrand.cw_status === 'installed'
		},
		filteredVisitors() {
			return this.filterIdentified ? this.visitors.filter(v => v.email) : this.visitors
		},
		installSnippet() {
			const key = (this.selectedBrand && this.selectedBrand.cw_site_key) || 'cw_your_site_key'
			const endpoint = this.endpoint
			const openTag = '<scr' + 'ipt>'
			const closeTag = '</scr' + 'ipt>'
			return [
				'<!-- MC2 Tracking. Paste in <head> or a GTM Custom HTML tag (fire on All Pages). -->',
				openTag,
				'(function () {',
				'  var SITE_KEY = "' + key + '";',
				'  var ENDPOINT = "' + endpoint + '";',
				'  var DOMAINS = ["townweb.com", "heygov.com", "clerkminutes.com"];',
				'  try {',
				'    var K = "_mc2vid";',
				'    var qs = new URLSearchParams(location.search);',
				'    var id = qs.get("_mc2ref") || localStorage.getItem(K) || (document.cookie.match(/(?:^|; )_mc2vid=([^;]+)/) || [])[1];',
				'    if (!id) { id = (crypto.randomUUID ? crypto.randomUUID() : String(Date.now()) + Math.random().toString(16).slice(2)); }',
				'    localStorage.setItem(K, id);',
				'    document.cookie = K + "=" + id + "; path=/; max-age=" + (60 * 60 * 24 * 400) + "; SameSite=Lax";',
				'    var via = qs.get("via");',
				'    if (qs.get("utm_campaign") === "affiliate" && qs.get("utm_source")) {',
				'      localStorage.setItem("mc2-affiliate-cta", "Friends of " + qs.get("utm_source") + " get a free month");',
				'    } else if (via) {',
				'      localStorage.setItem("mc2-affiliate-cta", "Friends at " + via + " get a free month");',
				'    }',
				'    var cta = localStorage.getItem("mc2-affiliate-cta");',
				'    if (cta) { document.querySelectorAll("[data-affiliate-customize]").forEach(function (el) { el.textContent = cta; }); }',
				'    document.addEventListener("click", function (e) {',
				'      var a = e.target && e.target.closest ? e.target.closest("a[href]") : null;',
				'      if (!a) return;',
				'      try {',
				'        var u = new URL(a.href, location.href);',
				'        if (u.hostname !== location.hostname && DOMAINS.some(function (d) { return u.hostname === d || u.hostname.endsWith("." + d); })) {',
				'          u.searchParams.set("_mc2ref", id); a.href = u.toString();',
				'        }',
				'      } catch (err) {}',
				'    }, true);',
				'    document.addEventListener("submit", function (e) {',
				'      try {',
				'        var f = e.target;',
				'        if (!f || !f.querySelector) return;',
				'        var em = f.querySelector("input[type=email], input[name=email]");',
				'        if (em && em.value) {',
				'          navigator.sendBeacon(ENDPOINT, new Blob([JSON.stringify({',
				'            site_key: SITE_KEY, visitor: id, url: location.href, event_type: "form_submit", email: em.value, ts: Date.now()',
				'          })], { type: "text/plain" }));',
				'        }',
				'      } catch (err) {}',
				'    }, true);',
				'    navigator.sendBeacon(ENDPOINT, new Blob([JSON.stringify({',
				'      site_key: SITE_KEY, visitor: id, url: location.href,',
				'      referrer: document.referrer, via: via || undefined, ts: Date.now()',
				'    })], { type: "text/plain" }));',
				'  } catch (e) {}',
				'})();',
				closeTag,
			].join('\n')
		},
	},
	created() {
		this.loadBrands()
	},
	methods: {
		loadBrands() {
			restApi
				.get('/marketing/brands')
				.then(({ data }) => {
					this.brands = Array.isArray(data) ? data : []
					this.pickDefault()
				})
				.catch(error => {
					this.brands = []
					this.errorMessage = 'Could not load brands.'
					console.log(error)
				})
		},
		pickDefault() {
			if (!this.brands.length) return
			let saved = null
			try {
				saved = parseInt(localStorage.getItem('mc2_cw_brand_id'), 10)
			} catch (e) {
				saved = null
			}
			const match = saved && this.brands.find(b => b.id === saved)
			this.selectedBrandId = match ? saved : this.brands[0].id
			this.loadBrand()
		},
		loadBrand() {
			try {
				localStorage.setItem('mc2_cw_brand_id', String(this.selectedBrandId))
			} catch (e) {
				// localStorage may be unavailable; brand persistence is best-effort.
			}
			this.loadSummary()
			this.loadVisitors()
		},
		loadSummary() {
			this.showSnippet = false
			if (!this.selectedBrand || !this.selectedBrand.id) {
				this.summary = { tracked_visitors: 0, identified_leads: 0, quotes: 0, cross_property: 0, recent: [] }
				this.summaryLoading = false
				return
			}
			const brandId = this.selectedBrand.id
			this.summaryLoading = true
			restApi
				.get('/marketing/tracking/summary', { params: { brand_id: brandId, days: 30 } })
				.then(({ data }) => {
					if (this.selectedBrandId !== brandId) return
					this.summary = {
						tracked_visitors: data.tracked_visitors || 0,
						identified_leads: data.identified_leads || 0,
						quotes: data.quotes || 0,
						cross_property: data.cross_property || 0,
						recent: data.recent || [],
					}
					this.summaryLoading = false
				})
				.catch(error => {
					if (this.selectedBrandId !== brandId) return
					this.summaryLoading = false
					this.summary = { tracked_visitors: 0, identified_leads: 0, quotes: 0, cross_property: 0, recent: [] }
					console.log(error)
				})
		},
		loadVisitors() {
			this.expandedId = null
			this.journeyEvents = []
			this.journeyLoading = false
			if (!this.selectedBrand || !this.selectedBrand.id) {
				this.visitors = []
				this.visitorsLoading = false
				return
			}
			const brandId = this.selectedBrand.id
			this.visitorsLoading = true
			restApi
				.get('/marketing/tracking/visitors', { params: { brand_id: brandId, days: 30 } })
				.then(({ data }) => {
					if (this.selectedBrandId !== brandId) return
					this.visitors = Array.isArray(data.visitors) ? data.visitors : []
					this.visitorsLoading = false
				})
				.catch(error => {
					if (this.selectedBrandId !== brandId) return
					this.visitorsLoading = false
					this.visitors = []
					console.log(error)
				})
		},
		toggleVisitor(id) {
			if (this.expandedId === id) {
				this.expandedId = null
				this.journeyLoading = false
				return
			}
			if (!this.selectedBrand || !this.selectedBrand.id) return
			this.expandedId = id
			this.journeyEvents = []
			this.journeyLoading = true
			restApi
				.get(`/marketing/tracking/visitor/${id}/journey`, { params: { brand_id: this.selectedBrand.id } })
				.then(({ data }) => {
					if (this.expandedId !== id) return
					this.journeyEvents = Array.isArray(data.events) ? data.events : []
					this.journeyLoading = false
				})
				.catch(error => {
					if (this.expandedId !== id) return
					this.journeyLoading = false
					this.journeyEvents = []
					console.log(error)
				})
		},
		toggleInstalled() {
			if (!this.selectedBrand || !this.selectedBrand.id) return
			const next = this.isInstalled ? 'not_installed' : 'installed'
			this.statusSaving = true
			restApi
				.put(`/marketing/brands/${this.selectedBrand.id}`, { cw_status: next })
				.then(({ data }) => {
					const idx = this.brands.findIndex(b => b.id === data.id)
					if (idx !== -1) this.$set(this.brands, idx, data)
					this.statusSaving = false
				})
				.catch(error => {
					this.statusSaving = false
					this.errorMessage = 'Could not update install status.'
					console.log(error)
				})
		},
		copySnippet() {
			navigator.clipboard.writeText(this.installSnippet).then(() => {
				this.copied = true
				setTimeout(() => (this.copied = false), 1500)
			})
		},
		formatDate(value) {
			if (!value) return '-'
			const d = new Date(value)
			if (isNaN(d.getTime())) return '-'
			return d.toLocaleDateString()
		},
		formatDateTime(value) {
			if (!value) return '-'
			const d = new Date(value)
			if (isNaN(d.getTime())) return '-'
			return d.toLocaleString()
		},
		shortRef(value) {
			if (!value) return '-'
			try {
				return new URL(value).hostname || value
			} catch (e) {
				return String(value).slice(0, 40)
			}
		},
		confidenceClass(c) {
			if (c === 'high') return 'bg-success'
			if (c === 'medium') return 'bg-primary'
			if (c === 'low') return 'bg-secondary'
			return 'bg-light text-dark'
		},
		formatLocation(r) {
			const parts = [r.geo_city, r.geo_region].filter(Boolean)
			return parts.length ? parts.join(', ') : '-'
		},
	},
}
</script>
